import React, { useState, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  FaStar, 
  FaPlay, 
  FaPause, 
  FaPaperPlane,
  FaCalendarAlt,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaClock,
  FaCheckCircle
} from 'react-icons/fa';
import { MessageSquare, Award, Shield, Truck, Clock, ThumbsUp } from 'lucide-react';
import ContactForm from './ContactForm';
import "../../Component/Home/theme.css";

const ContactPage = () => {
  const videoRef = useRef(null);
  const formRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);
  
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @import url('https://fonts.cdnfonts.com/css/bell-mt');
      @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
      
      .bell-mt-primary {
        font-family: 'Bell MT', serif !important;
      }
      
      .inter-secondary {
        font-family: 'Inter', sans-serif !important;
      }
      
      body {
        font-family: 'Inter', sans-serif;
      }
      
      h1, h2, h3, h4, h5, h6 {
        font-family: 'Bell MT', serif;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  useEffect(() => {
    if (window.location.hash === '#contact-form') {
      setTimeout(() => scrollToForm(), 100);
    }
    const handleHashChange = () => {
      if (window.location.hash === '#contact-form') scrollToForm();
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const scrollToForm = () => {
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const toggleVideo = () => {
    if (videoRef.current) {
      if (isPlaying) videoRef.current.pause();
      else videoRef.current.play();
      setIsPlaying(!isPlaying);
    }
  };

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(e => console.log("Autoplay prevented:", e));
    }
  }, []);

  const getCurrentDay = () => {
    const estNow = new Date().toLocaleString('en-US', { timeZone: 'America/New_York' });
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[new Date(estNow).getDay()];
  };

  const businessHours = [
    { day: 'Monday', hours: 'Closed', isClosed: true, shortDay: 'Mon' },
    { day: 'Tuesday', hours: '10:00 AM – 5:00 PM', isClosed: false, shortDay: 'Tue' },
    { day: 'Wednesday', hours: '10:00 AM – 5:00 PM', isClosed: false, shortDay: 'Wed' },
    { day: 'Thursday', hours: '10:00 AM – 5:00 PM', isClosed: false, shortDay: 'Thu' },
    { day: 'Friday', hours: '10:00 AM – 5:00 PM', isClosed: false, shortDay: 'Fri' },
    { day: 'Saturday', hours: '10:00 AM – 2:00 PM', isClosed: false, shortDay: 'Sat' },
    { day: 'Sunday', hours: 'Closed', isClosed: true, shortDay: 'Sun' }
  ];

  // Schema Data
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "HomeAndConstructionBusiness",
    "name": "Laminance Cabinetry",
    "description": "Premier custom kitchen cabinet and complete kitchen renovation services in New Jersey. Free consultation available.",
    "url": "https://laminance.com",
    "telephone": "+18624506069",
    "email": "Contactus@laminance.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "418 Rt 23",
      "addressLocality": "Franklin",
      "addressRegion": "NJ",
      "postalCode": "07416",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "41.1220",
      "longitude": "-74.5804"
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "10:00",
        "closes": "17:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Saturday",
        "opens": "10:00",
        "closes": "14:00"
      }
    ],
    "areaServed": {
      "@type": "State",
      "name": "New Jersey"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+18624506069",
      "contactType": "customer service",
      "email": "Contactus@laminance.com",
      "availableLanguage": ["English"]
    }
  };

  const localBusinessSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Laminance Cabinetry",
    "description": "Custom kitchen cabinets and complete kitchen renovation services in New Jersey. Free consultation and quotes.",
    "url": "https://laminance.com/contact",
    "telephone": "+18624506069",
    "priceRange": "$$",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "418 Rt 23",
      "addressLocality": "Franklin",
      "addressRegion": "NJ",
      "postalCode": "07416",
      "addressCountry": "US"
    }
  };

  const contactPageSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact Laminance Cabinetry | Free Kitchen Consultation",
    "description": "Contact Laminance Cabinetry for a free consultation on custom kitchen cabinets and complete kitchen renovations in New Jersey. Call, email, or fill out our contact form.",
    "url": "https://laminance.com/contact",
    "mainEntity": {
      "@type": "WebPageElement",
      "cssSelector": "#contact-form"
    }
  };

  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>Contact Laminance Cabinetry | Free Kitchen Consultation in New Jersey</title>
        <meta name="title" content="Contact Laminance Cabinetry | Free Kitchen Consultation in New Jersey | Custom Kitchen Cabinets" />
        <meta name="description" content="Contact Laminance Cabinetry for a free consultation on custom kitchen cabinets and complete kitchen renovations. Call (862) 450-6069 or email Contactus@laminance.com. Serving all of New Jersey." />
        <meta name="keywords" content="contact Laminance Cabinetry, kitchen consultation New Jersey, free kitchen design consultation, custom kitchen cabinets NJ, kitchen renovation contact, cabinet installation quote, Laminance contact" />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large" />
        <meta name="author" content="Laminance Cabinetry" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://laminance.com/contact" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://laminance.com/contact" />
        <meta property="og:title" content="Contact Laminance Cabinetry | Free Kitchen Consultation New Jersey" />
        <meta property="og:description" content="Get a free consultation for custom kitchen cabinets and complete kitchen renovations in New Jersey. Call, email, or fill out our contact form today!" />
        <meta property="og:image" content="https://laminance.com/assets/Images/logoimg.png" />
        <meta property="og:image:alt" content="Laminance Cabinetry - Contact us for free kitchen consultation" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Laminance Cabinetry" />
        <meta property="og:locale" content="en_US" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://laminance.com/contact" />
        <meta name="twitter:title" content="Contact Laminance Cabinetry | Free Kitchen Consultation" />
        <meta name="twitter:description" content="Free consultation for custom kitchen cabinets in New Jersey. Contact us today!" />
        <meta name="twitter:image" content="https://laminance.com/assets/Images/logoimg.png" />
        <meta name="twitter:site" content="@laminance" />
        
        {/* Geo Meta Tags */}
        <meta name="geo.region" content="US-NJ" />
        <meta name="geo.placename" content="Franklin" />
        <meta name="geo.position" content="41.1220;-74.5804" />
        <meta name="ICBM" content="41.1220, -74.5804" />
        
        {/* Additional SEO */}
        <meta name="revisit-after" content="7 days" />
        <meta name="language" content="English" />
        
        {/* Structured Data */}
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(localBusinessSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(contactPageSchema)}</script>
      </Helmet>

      <div className="min-h-screen inter-secondary" style={{ background: "var(--gradient-bg)" }}>
        
        {/* Hidden SEO Content for Crawlers */}
        <div className="sr-only" aria-hidden="true">
          <h1>Contact Laminance Cabinetry for Custom Kitchen Cabinets in New Jersey</h1>
          <p>Laminance Cabinetry offers free consultation for custom kitchen cabinets, acrylic cabinets, shaker cabinets, and complete kitchen renovations. Serving all of New Jersey including Franklin, Bergen County, Essex County, Morris County, and Passaic County.</p>
          <p>Contact us by phone at (862) 450-6069, email at Contactus@laminance.com, or visit our showroom at 418 Rt 23, Franklin, NJ, 07416. Our business hours are Tuesday through Friday 10 AM to 5 PM, and Saturday 10 AM to 2 PM.</p>
        </div>

        {/* Hero Section */}
        <section className="pt-12 md:pt-16 pb-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 mx-auto"
                style={{ background: "var(--bg-primary-10)", border: "1px solid var(--border-primary-20)" }}
              >
                <MessageSquare size={14} style={{ color: "var(--color-primary-400)" }} />
                <span className="text-sm font-medium" style={{ color: "var(--color-primary-400)" }}>Get Started</span>
              </div>
              
              <h1 className="bell-mt-primary text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4"
                style={{ color: "var(--color-primary-500)" }}
              >
                Contact <span style={{ background: "var(--gradient-primary)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>Laminance Cabinetry</span>
              </h1>
              
              <p className="inter-secondary text-base sm:text-lg md:text-xl max-w-3xl mx-auto" style={{ color: "var(--color-gray-600)" }}>
                Ready to transform your kitchen? Our team is here to help. Get a free consultation and quote today.
              </p>
              
              <div className="flex justify-center mt-6">
                <div className="w-24 h-1 rounded-full" style={{ background: "var(--gradient-primary)" }}></div>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Contact Info Banner */}
        {/* <section className="py-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { icon: FaPhone, text: "Call Us", value: "(862) 450-6069", link: "tel:8624506069", desc: "Mon-Sat 10AM-5PM" },
                { icon: FaEnvelope, text: "Email Us", value: "Contactus@laminance.com", link: "mailto:Contactus@laminance.com", desc: "Response within 24hrs" },
                { icon: FaMapMarkerAlt, text: "Visit Us", value: "418 Rt 23, Franklin, NJ", link: "https://maps.google.com", desc: "Showroom by appointment" }
              ].map((item, idx) => {
                const Icon = item.icon;
                return (
                  <a key={idx} href={item.link} className="block group" target={idx === 2 ? "_blank" : "_self"} rel="noopener noreferrer">
                    <div className="flex items-center gap-4 p-4 rounded-xl bg-white shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                      style={{ border: "1px solid var(--border-light)" }}
                    >
                      <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform"
                        style={{ background: "var(--gradient-primary)" }}
                      >
                        <Icon className="text-white text-xl" />
                      </div>
                      <div>
                        <div className="inter-secondary text-xs font-semibold" style={{ color: "var(--color-gray-500)" }}>{item.text}</div>
                        <div className="bell-mt-primary text-sm font-bold" style={{ color: "var(--color-primary-500)" }}>{item.value}</div>
                        <div className="inter-secondary text-xs" style={{ color: "var(--color-gray-500)" }}>{item.desc}</div>
                      </div>
                    </div>
                  </a>
                );
              })}
            </div>
          </div>
        </section> */}

        {/* Contact Form Section */}
        <section id="contact-form" ref={formRef} className="py-8 md:py-12 px-4 sm:px-6 lg:px-8 scroll-mt-20">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col lg:grid lg:grid-cols-3 gap-6 sm:gap-8">
              {/* Contact Form */}
              <div className="order-1 lg:order-2 lg:col-span-2">
                <div className="rounded-2xl sm:rounded-3xl border p-4 sm:p-6 md:p-8 shadow-lg"
                  style={{ backgroundColor: "var(--color-white)", borderColor: "var(--border-light)", boxShadow: "var(--shadow-medium)" }}
                >
                  <h2 className="bell-mt-primary text-xl sm:text-2xl font-bold mb-4" style={{ color: "var(--color-primary-500)" }}>
                    Send us a Message
                  </h2>
                  <p className="inter-secondary text-sm mb-6" style={{ color: "var(--color-gray-600)" }}>
                    Fill out the form below and our team will contact you within 24 hours to discuss your kitchen project.
                  </p>
                  <ContactForm />
                </div>
              </div>

              {/* Contact Info Sidebar */}
              <div className="order-2 lg:order-1 lg:col-span-1">
                <div className="rounded-2xl sm:rounded-3xl p-6 sm:p-8 h-full relative overflow-hidden"
                  style={{ background: "var(--color-primary-500)" }}
                >
                  <div className="absolute top-0 right-0 w-32 h-32 rounded-full translate-x-1/2 -translate-y-1/2 opacity-20 blur-3xl" 
                    style={{ backgroundColor: "var(--color-white)" }} aria-hidden="true"
                  ></div>
                  <div className="absolute bottom-0 left-0 w-32 h-32 rounded-full -translate-x-1/2 translate-y-1/2 opacity-20 blur-3xl" 
                    style={{ backgroundColor: "var(--color-white)" }} aria-hidden="true"
                  ></div>

                  <div className="relative z-10">
                    <div className="mb-8">
                      <h3 className="bell-mt-primary text-xl font-bold mb-4 flex items-center gap-2" style={{ color: "var(--color-white)" }}>
                        <FaClock /> Business Hours
                      </h3>
                      <div className="space-y-2">
                        {businessHours.map((day, index) => {
                          const isToday = day.day === getCurrentDay();
                          return (
                            <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-white">
                              <div className="flex items-center space-x-3">
                                <span className="text-lg">{day.isClosed ? "❌" : "🕒"}</span>
                                <span className="inter-secondary font-medium" style={{ color: "var(--color-primary-500)" }}>
                                  {day.shortDay}{isToday && " (Today)"}
                                </span>
                              </div>
                              <span className="inter-secondary font-semibold text-sm" style={{ color: "var(--color-primary-500)" }}>
                                {day.hours}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="mb-8">
                      <h3 className="bell-mt-primary text-xl font-bold mb-4 flex items-center gap-2" style={{ color: "var(--color-white)" }}>
                        <Award size={20} /> Why Choose Us
                      </h3>
                      <div className="space-y-4">
                        {[
                          { icon: "👥", title: 'Expert Team', desc: '15+ years of specialized experience' },
                          { icon: "🛡️", title: 'Quality Warranty', desc: '5-year limited warranty on all cabinets' },
                          { icon: "🚀", title: 'Fast Turnaround', desc: '2-4 week completion time' },
                          { icon: "💰", title: 'Free Quote', desc: 'No obligation consultation' }
                        ].map((item, index) => (
                          <div key={index} className="flex items-start space-x-3">
                            <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-white">
                              <span style={{ color: "var(--color-primary-500)", fontSize: "1.25rem" }}>{item.icon}</span>
                            </div>
                            <div>
                              <h4 className="bell-mt-primary font-semibold text-sm" style={{ color: "var(--color-white)" }}>{item.title}</h4>
                              <p className="inter-secondary text-xs mt-1" style={{ color: "rgba(255,255,255,0.9)" }}>{item.desc}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h3 className="bell-mt-primary text-xl font-bold mb-4 flex items-center gap-2" style={{ color: "var(--color-white)" }}>
                        <FaPhone /> Contact Directly
                      </h3>
                      <div className="space-y-3">
                        <a href="tel:8624506069"
                          className="inter-secondary flex items-center justify-between p-3 rounded-xl transition-all bg-white"
                          style={{ color: "var(--color-primary-500)" }}
                        >
                          <span className="font-medium">Call Now</span>
                          <span className="font-bold text-sm">(862) 450-6069</span>
                        </a>
                        <a href="mailto:Contactus@laminance.com"
                          className="inter-secondary flex items-center justify-between p-3 rounded-xl transition-all bg-white"
                          style={{ color: "var(--color-primary-500)" }}
                        >
                          <span className="font-medium">Email Us</span>
                          <span className="font-bold text-sm">Contactus@laminance.com</span>
                        </a>
                        <a href="https://maps.google.com/?q=418+Rt+23+Franklin+NJ+07416"
                          target="_blank" rel="noopener noreferrer"
                          className="inter-secondary flex items-center justify-between p-3 rounded-xl transition-all bg-white"
                          style={{ color: "var(--color-primary-500)" }}
                        >
                          <span className="font-medium">Visit Us</span>
                          <span className="font-bold text-sm">Get Directions →</span>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Map Section */}
        <section className="py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-6">
              <h2 className="bell-mt-primary text-2xl font-bold" style={{ color: "var(--color-primary-500)" }}>
                Visit Our Showroom
              </h2>
              <p className="inter-secondary text-sm" style={{ color: "var(--color-gray-600)" }}>
                418 Rt 23, Franklin, NJ 07416
              </p>
            </div>
            <div className="rounded-xl overflow-hidden shadow-lg h-64 md:h-96">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3018.123456789!2d-74.5804!3d41.1220!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c3a8b9c7d5e6f7%3A0x123456789abcdef!2s418%20Rt%2023%2C%20Franklin%2C%20NJ%2007416!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
                width="100%" 
                height="100%" 
                style={{ border: 0 }} 
                allowFullScreen 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                title="Laminance Cabinetry Showroom Location - Franklin, New Jersey"
                aria-label="Google Maps showing Laminance Cabinetry showroom location at 418 Rt 23, Franklin, NJ"
              ></iframe>
            </div>
          </div>
        </section>

        {/* SEO Content Section */}
        <section className="py-8 md:py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="bell-mt-primary text-2xl font-bold mb-4" style={{ color: "var(--color-primary-500)" }}>
              Get Your Free Kitchen Consultation Today
            </h2>
            <p className="inter-secondary text-base mb-4" style={{ color: "var(--color-gray-600)" }}>
              Whether you're looking for <strong>custom kitchen cabinets</strong>, <strong>acrylic cabinets</strong>, 
              <strong> shaker cabinets</strong>, or a <strong>complete kitchen renovation</strong>, Laminance Cabinetry is here to help. 
              Serving all of <strong>New Jersey</strong>, we provide free in-home consultations, 3D design services, and competitive pricing.
            </p>
            <p className="inter-secondary text-base" style={{ color: "var(--color-gray-600)" }}>
              Contact us today to schedule your free consultation and take the first step toward your dream kitchen.
            </p>
            
            <div className="mt-6 flex flex-wrap justify-center gap-2">
              {["Free Kitchen Consultation", "Custom Kitchen Cabinets", "Kitchen Renovation NJ", "Cabinet Installation", "3D Kitchen Design", "Free Quote"].map((keyword, idx) => (
                <span key={idx} className="inter-secondary text-xs px-3 py-1 rounded-full"
                  style={{ backgroundColor: "var(--bg-primary-10)", color: "var(--color-primary-600)" }}
                >
                  {keyword}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Floating Back to Form Button */}
        <div className="lg:hidden fixed bottom-6 right-6 z-50">
          <button onClick={scrollToForm}
            className="w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-transform hover:scale-110"
            style={{ background: "var(--gradient-primary)" }}
            aria-label="Scroll to contact form"
          >
            <FaPaperPlane className="text-white text-xl" />
          </button>
        </div>
      </div>
    </>
  );
};

export default ContactPage;